import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, MessageBar, MessageBarBody, Spinner, Tab, TabList, Toast, ToastTitle, useToastController } from '@fluentui/react-components';
import { LockClosed16Regular } from '@fluentui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../../../commons/component/PageHeader/PageHeader';
import { AppState } from '../../../redux/reducers';
import * as ContractReviewStore from '../../../redux/middleware/ContractReview.Middleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';
import * as ContractAddendumStore from '../../../redux/middleware/ContractAddendum.Middleware';
import * as ContractStageStore from '../../../redux/middleware/ContractStageMiddleware';
import * as ContractConceptStore from '../../../redux/middleware/ContractConceptMiddleware';

import { ConceptVerdictItem, ContractReviewFinding, FindingDecision } from '../../../models/Entities/Contracts/ContractReview';
import { ErrorType } from '../../../models/HttpError';
import {
  canDecide,
  canUndoReview,
  countByWorkState,
  defaultWorkState,
  errorText,
  filterByView,
  filterFindings,
  getApplicableFindings,
  getUndoableFindings,
  isAddendumCreate,
  isChangeFinding,
  isReviewActive,
  ReviewView,
  sortForWork,
  WorkStateFilter
} from '../../../components/Contracts/ContractReview/ContractReview.Functions';
import { useContractReviewPolling, useFindingFilters, useReviewCompletionToast } from '../../../components/Contracts/ContractReview/ContractReview.Hooks';
import { ContractReviewOverview } from '../../../components/Contracts/ContractReview/ContractReviewOverview/ContractReviewOverview.Component';
import { ContractReviewWarnings } from '../../../components/Contracts/ContractReview/ContractReviewWarnings/ContractReviewWarnings.Component';
import { ContractReviewDetailsDrawer } from '../../../components/Contracts/ContractReview/ContractReviewDetailsDrawer/ContractReviewDetailsDrawer.Component';
import { ContractReviewWorkToolbar } from '../../../components/Contracts/ContractReview/ContractReviewWorkList/ContractReviewWorkToolbar.Component';
import { ContractReviewWorkList } from '../../../components/Contracts/ContractReview/ContractReviewWorkList/ContractReviewWorkList.Component';
import { ContractReviewDecisionDialog } from '../../../components/Contracts/ContractReview/ContractReviewDecisionDialog/ContractReviewDecisionDialog.Component';
import { ContractReviewBulkBar } from '../../../components/Contracts/ContractReview/ContractReviewBulkBar/ContractReviewBulkBar.Component';
import { ContractReviewApplyDialog } from '../../../components/Contracts/ContractReview/ContractReviewApply/ContractReviewApplyDialog.Component';
import { ContractReviewApplyResult } from '../../../components/Contracts/ContractReview/ContractReviewApply/ContractReviewApplyResult.Component';
import { ContractReviewConceptsTab } from '../../../components/Contracts/ContractReview/ContractReviewConceptsTab/ContractReviewConceptsTab.Component';
import { ContractReviewNewAddendumHeader } from '../../../components/Contracts/ContractReview/ContractReviewNewAddendumHeader/ContractReviewNewAddendumHeader.Component';
import { ContractReviewUndoDialog, UndoTarget } from '../../../components/Contracts/ContractReview/ContractReviewUndoDialog/ContractReviewUndoDialog.Component';
import '../../../components/Contracts/ContractReview/ContractReview.Page.css';

import { addendumName, addendumsToRefresh, listFindings, normalizeTab, pruneSelection, ReviewTab } from './ContractReview.Functions';

/*
 * Página de la revisión con IA: /contracts/:ContractId/reviews/:ReviewId (US3, US4, US5, US7, US9; spec 015).
 * Pensada para alguien que no conoce la herramienta, en tres niveles:
 *   1) qué pasó y qué hay que hacer ahora (cabecera con pasos, números por estado y el botón principal),
 *   2) avisos de la lectura del documento, en lenguaje de usuario,
 *   3) la lista de trabajo: una tarjeta por cambio o aviso, con Aceptar / Rechazar en palabras; y "Concepto por concepto".
 */

const ContractReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { dispatchToast } = useToastController('PrebillerToast');
  const params = useParams();
  const contractId = Number(params.ContractId);
  const reviewId = params.ReviewId ?? '';

  const review = useSelector((s: AppState) => s.ContractReview?.byId?.[reviewId]);
  const reviewState = useSelector((s: AppState) => s.ContractReview);
  const contract = useSelector((s: AppState) => s.Contract?.list?.find(c => c.id === contractId));
  const addendums = contract?.addendums;

  const [filters, setFilters, query, setParam] = useFindingFilters();
  const tab: ReviewTab = normalizeTab(query.get('tab'));

  const [view, setView] = useState<ReviewView>('all');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [pendingDecision, setPendingDecision] = useState<{ finding: ContractReviewFinding; decision: FindingDecision } | undefined>(undefined);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | undefined>(undefined);
  const [bulkResult, setBulkResult] = useState<{ updated: number; error?: ErrorType } | undefined>(undefined);
  const [applyOpen, setApplyOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [conceptLabel, setConceptLabel] = useState<string | undefined>(undefined);
  const [undoTarget, setUndoTarget] = useState<UndoTarget | undefined>(undefined);

  const load = useCallback(() => dispatch(ContractReviewStore.actionCreators.GetContractReviewById(reviewId)), [dispatch, reviewId]);

  useEffect(() => {
    load();
    if (!contract) dispatch(ContractStore.actionCreators.GetContractById(contractId));
    dispatch(ContractAddendumStore.actionCreators.GetContractAddendumsByContractId(contractId));
    return () => {
      dispatch(ContractReviewStore.actionCreators.ClearApplyResult());
      dispatch(ContractReviewStore.actionCreators.ClearUndoResult());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewId, contractId]);

  useContractReviewPolling(review ? [review] : [], false, load);

  const noticeText = (status: 'Completed' | 'Failed' | 'Applied', r: { appliedCount: number }) =>
    status === 'Applied'
      ? t('contractReview.toast.applied', { count: r.appliedCount })
      : status === 'Completed'
        ? t('contractReview.toast.completed')
        : t('contractReview.toast.failed');

  const notice = useReviewCompletionToast(review ? [review] : [], n =>
    dispatchToast(
      <Toast>
        <ToastTitle>{noticeText(n.status, n.review)}</ToastTitle>
      </Toast>,
      { intent: n.status === 'Failed' ? 'error' : 'success' }
    )
  );

  // La lista de trabajo (en NewAddendum, sin el alta ni el ajuste de la base, que van en su cabecera).
  const findings = useMemo(() => listFindings(review), [review]);
  const overviewCounts = useMemo(() => countByWorkState(review?.findings ?? []), [review]);
  const listCounts = useMemo(() => countByWorkState(findings), [findings]);
  const currentState: WorkStateFilter = filters.state ?? defaultWorkState(findings);
  const base = useMemo(() => filterFindings(findings, { ...filters, state: currentState }), [findings, filters, currentState]);
  const changesCount = useMemo(() => base.filter(isChangeFinding).length, [base]);
  const visible = useMemo(() => sortForWork(filterByView(base, view)), [base, view]);
  const hasConceptFilter = filters.contractConceptId !== undefined || (filters.findingIds?.length ?? 0) > 0;
  const narrowed = filters.text.trim().length > 0 || view !== 'all' || hasConceptFilter;

  const decidableIds = useMemo(() => (review ? findings.filter(f => canDecide(review, f)).map(f => f.id) : []), [review, findings]);
  const visibleDecidable = useMemo(() => (review ? visible.filter(f => canDecide(review, f)).map(f => f.id) : []), [review, visible]);

  useEffect(() => setSelected(prev => pruneSelection(prev, decidableIds)), [decidableIds]);

  const baseName = addendumName(addendums, review?.mode === 'NewAddendum' ? review?.baseAddendumId : review?.addendumId);
  const applicableCount = getApplicableFindings(review).length;

  const decide = (finding: ContractReviewFinding, decision: FindingDecision, comment: string | null) =>
    dispatch(ContractReviewStore.actionCreators.UpdateFindingDecision(reviewId, finding.id, decision, comment));

  const onBulk = async (decision: FindingDecision, comment: string | null) => {
    setBulkResult(undefined);
    setBulkProgress({ done: 0, total: selected.length });
    const updated: number = await dispatch(
      ContractReviewStore.actionCreators.UpdateFindingDecisionBulk(reviewId, selected, decision, comment, (done, total) => setBulkProgress({ done, total }))
    );
    setBulkProgress(undefined);
    setBulkResult({ updated: updated ?? 0, error: undefined });
    setSelected([]);
  };

  const bulkError = reviewState?.bulkError;

  const onApply = async () => {
    await dispatch(ContractReviewStore.actionCreators.ApplyContractReview(reviewId));
    setApplyOpen(false);
  };

  const applyResult = reviewState?.applyResult;
  useEffect(() => {
    if (!applyResult || applyResult.reviewId !== reviewId || !review) return;
    setResultOpen(true);
    addendumsToRefresh(review, applyResult.createdAddendumId).forEach(addendumId => {
      dispatch(ContractStageStore.actionCreators.GetAllContractStagesByAddendumId(contractId, addendumId));
      dispatch(ContractConceptStore.actionCreators.GetAllContractsConceptByAddendum(contractId, addendumId));
    });
    dispatch(ContractAddendumStore.actionCreators.GetContractAddendumsByContractId(contractId));
    dispatch(ContractReviewStore.actionCreators.GetContractParametrizationStatus(contractId));
    dispatch(ContractReviewStore.actionCreators.GetContractReviews(contractId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyResult]);

  // Deshacer (spec 015; Contracts 021 §4): después se refrescan las adendas tocadas, como al aplicar.
  const refreshAfterUndo = () => {
    if (!review) return;
    addendumsToRefresh(review, review.createdAddendumId).forEach(addendumId => {
      dispatch(ContractStageStore.actionCreators.GetAllContractStagesByAddendumId(contractId, addendumId));
      dispatch(ContractConceptStore.actionCreators.GetAllContractsConceptByAddendum(contractId, addendumId));
    });
    dispatch(ContractAddendumStore.actionCreators.GetContractAddendumsByContractId(contractId));
    dispatch(ContractReviewStore.actionCreators.GetContractReviews(contractId));
  };

  const onConfirmUndo = async () => {
    const target = undoTarget;
    if (!target) return;
    if (target.kind === 'finding') {
      setUndoTarget(undefined);
      await dispatch(ContractReviewStore.actionCreators.UndoFinding(reviewId, target.finding.id));
    } else {
      await dispatch(ContractReviewStore.actionCreators.UndoContractReview(reviewId));
      setUndoTarget(undefined);
    }
    refreshAfterUndo();
  };

  const undoResult = reviewState?.undoResult;
  useEffect(() => {
    if (!undoResult || undoResult.reviewId !== reviewId) return;
    const undone = undoResult.undoneCount ?? undoResult.results.filter(r => r.applyStatus === 'Undone').length;
    const notUndone = undoResult.results.length - undone;
    dispatchToast(
      <Toast>
        <ToastTitle>
          {notUndone > 0 ? t('contractReview.undo.toastPartial', { undone, notUndone }) : t('contractReview.undo.toastDone', { count: undone })}
        </ToastTitle>
      </Toast>,
      { intent: notUndone > 0 ? 'warning' : 'success' }
    );
    dispatch(ContractReviewStore.actionCreators.ClearUndoResult());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoResult]);

  const scrollToWork = () => document.getElementById('review-work')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });

  const goToState = (state: WorkStateFilter) => {
    setView('all');
    setFilters({ ...filters, state }, { tab: undefined });
    scrollToWork();
  };

  const viewConceptFindings = (verdict: ConceptVerdictItem) => {
    setConceptLabel(verdict.label);
    setView('all');
    setFilters({ ...filters, state: 'all', contractConceptId: verdict.contractConceptId ?? undefined, findingIds: verdict.findingIds }, { tab: undefined });
    scrollToWork();
  };

  const clearNarrowing = () => {
    setView('all');
    setConceptLabel(undefined);
    setFilters({ ...filters, text: '', contractConceptId: undefined, findingIds: undefined });
  };

  const header = <PageHeader title={t('contractReview.page.title')} subtitle={contract?.contractDescription ?? undefined} />;

  if (!review) {
    const failed = reviewState?.failOnGettingReview && reviewState?.loadingReviewId !== reviewId;
    return (
      <>
        {header}
        <div className="crv">
          {failed ? (
            <MessageBar intent="error" layout="multiline" role="alert">
              <MessageBarBody>
                {reviewState?.reviewError?.ErrorCode === 404 ? t('contractReview.errors.notFound') : errorText(reviewState?.reviewError) ?? t('contractReview.errors.loadReview')}
              </MessageBarBody>
              <Button onClick={load}>{t('contractReview.history.retryLoad')}</Button>
            </MessageBar>
          ) : (
            <Spinner label={t('contractReview.page.loading')} />
          )}
        </div>
      </>
    );
  }

  const active = isReviewActive(review);
  const finished = !active && review.status !== 'Failed';
  const isNewAddendum = review.mode === 'NewAddendum';
  const applyLabel = isNewAddendum ? t('contractReview.apply.createAddendum') : t('contractReview.apply.button', { count: applicableCount });
  const canApply = finished && (isNewAddendum || applicableCount > 0);

  return (
    <>
      {header}
      <main className="crv" aria-labelledby="review-title">
        <ContractReviewOverview
          review={review}
          addendumName={baseName}
          counts={overviewCounts}
          applicableCount={applicableCount}
          activeState={finished ? currentState : undefined}
          onGoToState={goToState}
          onApply={finished ? () => setApplyOpen(true) : undefined}
          applyLabel={applyLabel}
          applyDisabled={!canApply}
          onUndo={
            canUndoReview(review)
              ? () =>
                  setUndoTarget({
                    kind: 'review',
                    count: getUndoableFindings(review).length,
                    createsAddendum: isNewAddendum && getUndoableFindings(review).some(isAddendumCreate)
                  })
              : undefined
          }
          undoBusy={!!reviewState?.isUndoing}
          onOpenDetails={() => setDetailsOpen(true)}
        />

        <div role="status" aria-live="polite" className="crv-sr-only">
          {notice ? noticeText(notice.status, notice.review) : ''}
        </div>

        {review.status === 'Failed' && (
          <MessageBar intent="error" layout="multiline" role="alert">
            <MessageBarBody>
              {t('contractReview.page.failed')} {review.errorMessage}
            </MessageBarBody>
          </MessageBar>
        )}
        {reviewState?.undoError && (
          <MessageBar intent="error" layout="multiline" role="alert" data-testid="review-undo-error">
            <MessageBarBody>{t('contractReview.undo.reviewError', { message: errorText(reviewState.undoError) ?? t('contractReview.errors.generic') })}</MessageBarBody>
          </MessageBar>
        )}
        {reviewState?.applyError && (
          <MessageBar intent="error" layout="multiline" role="alert">
            <MessageBarBody>{t('contractReview.apply.error', { message: errorText(reviewState.applyError) ?? t('contractReview.errors.generic') })}</MessageBarBody>
          </MessageBar>
        )}
        {review.baseAddendumIsBlocked === true && (
          <MessageBar intent="warning" layout="multiline" icon={<LockClosed16Regular />} data-testid="addendum-blocked">
            <MessageBarBody>{t('contractReview.addendumBlocked')}</MessageBarBody>
          </MessageBar>
        )}

        <ContractReviewWarnings warnings={review.warnings ?? []} />

        {finished && (
          <section className="crv-work" id="review-work" aria-label={t('contractReview.page.tabsLabel')}>
            <TabList selectedValue={tab} onTabSelect={(_, data) => setParam('tab', data.value === 'findings' ? undefined : String(data.value))} aria-label={t('contractReview.page.tabsLabel')}>
              <Tab value="findings">{t('contractReview.page.tabs.findings')}</Tab>
              {review.scopes.includes('ConceptReview') && <Tab value="concepts">{t('contractReview.page.tabs.concepts')}</Tab>}
            </TabList>

            {tab === 'findings' && (
              <div className="crv-work__panel" role="tabpanel" aria-label={t('contractReview.page.tabs.findings')}>
                {isNewAddendum && (
                  <ContractReviewNewAddendumHeader
                    review={review}
                    baseAddendumName={baseName}
                    deciding={reviewState?.decidingFindingIds}
                    onDecide={(finding, decision) => setPendingDecision({ finding, decision })}
                  />
                )}

                {findings.length > 0 && (
                  <ContractReviewWorkToolbar
                    counts={listCounts}
                    state={currentState}
                    onStateChange={state => setFilters({ ...filters, state })}
                    view={view}
                    onViewChange={setView}
                    changesCount={changesCount}
                    observationsCount={base.length - changesCount}
                    text={filters.text}
                    onTextChange={text => setFilters({ ...filters, text })}
                    shown={visible.length}
                    total={findings.length}
                    conceptLabel={hasConceptFilter ? conceptLabel ?? '—' : undefined}
                    onRemoveConcept={hasConceptFilter ? clearNarrowing : undefined}
                    selectableCount={decidableIds.length > 0 ? visibleDecidable.length : undefined}
                    selectedCount={selected.length}
                    onSelectAll={() => setSelected(Array.from(new Set([...selected, ...visibleDecidable])))}
                    onClearSelection={() => setSelected([])}
                  />
                )}

                <ContractReviewWorkList
                  review={review}
                  findings={visible}
                  state={currentState}
                  filtered={narrowed}
                  nothingAtAll={findings.length === 0}
                  onClearFilters={clearNarrowing}
                  onViewAll={() => setFilters({ ...filters, state: 'all' })}
                  applyAction={canApply ? { label: applyLabel, onClick: () => setApplyOpen(true) } : undefined}
                  selectedIds={selected}
                  decidingIds={reviewState?.decidingFindingIds}
                  decisionErrors={reviewState?.decisionErrors}
                  onSelect={decidableIds.length > 0 ? (id, checked) => setSelected(checked ? Array.from(new Set([...selected, id])) : selected.filter(x => x !== id)) : undefined}
                  onDecide={(finding, decision) => setPendingDecision({ finding, decision })}
                  onUndo={finding => setUndoTarget({ kind: 'finding', finding })}
                  undoingIds={reviewState?.undoingFindingIds}
                  undoErrors={reviewState?.undoErrors}
                />

                {decidableIds.length > 0 && selected.length > 0 && (
                  <div className="crv-bulk">
                    <ContractReviewBulkBar
                      selectedCount={selected.length}
                      selectableVisibleIds={visibleDecidable}
                      busy={!!reviewState?.isBulkDeciding}
                      progress={bulkProgress}
                      lastResult={bulkError ? { updated: 0, error: bulkError } : bulkResult}
                      onSelectAllVisible={ids => setSelected(Array.from(new Set([...selected, ...ids])))}
                      onClear={() => setSelected([])}
                      onDecide={onBulk}
                    />
                  </div>
                )}
              </div>
            )}
            {tab === 'concepts' && (
              <div className="crv-work__panel" role="tabpanel" aria-label={t('contractReview.page.tabs.concepts')}>
                <ContractReviewConceptsTab review={review} onViewFindings={viewConceptFindings} />
              </div>
            )}
          </section>
        )}
      </main>

      <ContractReviewDetailsDrawer open={detailsOpen} review={review} addendumName={baseName} onDismiss={() => setDetailsOpen(false)} />

      <ContractReviewDecisionDialog
        open={pendingDecision !== undefined}
        decision={pendingDecision?.decision ?? 'Accepted'}
        initialComment={pendingDecision?.finding.decisionComment ?? ''}
        busy={false}
        onCancel={() => setPendingDecision(undefined)}
        onConfirm={comment => {
          if (pendingDecision) decide(pendingDecision.finding, pendingDecision.decision, comment);
          setPendingDecision(undefined);
        }}
      />
      <ContractReviewUndoDialog target={undoTarget} busy={!!reviewState?.isUndoing} onConfirm={onConfirmUndo} onCancel={() => setUndoTarget(undefined)} />
      <ContractReviewApplyDialog
        open={applyOpen}
        review={review}
        baseAddendumName={baseName}
        busy={!!reviewState?.isApplying}
        onConfirm={onApply}
        onCancel={() => setApplyOpen(false)}
      />
      {applyResult && applyResult.reviewId === reviewId && (
        <ContractReviewApplyResult
          open={resultOpen}
          result={applyResult}
          findings={review.findings ?? []}
          onClose={() => {
            setResultOpen(false);
            dispatch(ContractReviewStore.actionCreators.ClearApplyResult());
          }}
          onGoToNewAddendum={addendumId => {
            dispatch(ContractReviewStore.actionCreators.ClearApplyResult());
            navigate(`/contracts/${contractId}?addendumId=${addendumId}`);
          }}
        />
      )}
    </>
  );
};

export default ContractReviewPage;
